import { React, PT, NavLink, WithData } from '@gasbuddy/react';
import bases from 'bases';
import moment from 'moment';
import BN from 'bignumber.js';
import JSONTree from 'react-json-tree';
import { Loader, Dimmer, Segment, Table, Divider, Button, Label, Modal, Icon, Popup, Message, Confirm } from 'semantic-ui-react';
import {
  getRecentFailedChargeAttempts,
  shouldShowBillingBlockedPill,
} from '../../../lib/charge-attempts';
import { fetch } from '../../../client/lib/fetch';
import { Auth, Nav } from '../../components';
import { Role } from '../../roles';
import { TransactionTools } from '../../devtools/TransactionTools';
import { RefundModal } from '../../components/payments/refundModal';
import { MoveModal } from '../../components/payments/moveModal';
import { withRouter } from '../../../../node_modules/react-router-dom';
import { TxDetailModal } from '../../components/payments/txDetailModal';
import { DelayModal } from '../../components/payments/delayModal';
import { AwardGasbackModal } from '../../components/payments/awardGasbackModal';

function splitIndex(data, splitId) {
  if (!splitId) {
    return '-';
  }
  return (data.splits.findIndex(s => s.split_id === splitId) + 1) || '?';
}

function pre(obj) {
  if (!obj) {
    return '';
  }
  return (
    <pre>{JSON.stringify(obj, null, '\t')}</pre>
  );
}

function addr(a) {
  return (
    <div>
      <div>{a.line_1}</div>
      {a.line_2 && (
        <div>{a.line_2}</div>
      )}
      <div>
        {a.locality}
,
        {' '}
        {a.region}
        {' '}
        {a.postal_code}
      </div>
    </div>
  );
}

function accountTagLine(account) {
  if (!account) {
    return '-';
  }
  const email = (account.identifiers.find(i => i.namespace === 'email') || account.identifiers[0])
    .identifier;
  const cardName = bases.toAlphabet(account.id, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ');
  return `${email} (Card name A${cardName})`;
}

function splitRowAttributes(split) {
  if (split.split_type === 'payment' && new BN(split.amount).gt(0)) {
    return { error: true };
  }
  if (split.split_type === 'payment'
    && (split.split_status === 'voided' || split.split_status === 'declined')) {
    return { error: true };
  }
  return {};
}

function forceBilled(tx) {
  return tx && tx.extra_data && tx.extra_data.force_bill;
}

function isADSTransaction(tx) {
  return tx.splits?.some(s => s.counterparty === 'ads');
}

function wasBilled(tx) {
  if (!tx.splits || !tx.amount) {
    return false;
  }
  return Boolean(tx.splits.find(s => s.split_type === 'payment' && s.counterparty === 'dwolla')
      || tx.splits.find(s => s.split_type === 'payment' && s.counterparty === 'stripe')
      || tx.splits.find(s => s.split_type === 'payment' && s.counterparty === 'customer-credit')
      || tx.splits.find(s => s.split_type === 'payment' && s.counterparty === 'line-of-credit'));
}

function canRebill(tx) {
  if (!tx.splits || !tx.amount) {
    return false;
  }
  if (!tx.splits.find(s => s.split_type === 'purchase')) {
    return false;
  }
  const billed = tx.splits
    .filter(s => !(s.counterparty === 'dwolla' && !s.external_transaction_id))
    .filter(s => s.split_status !== 'voided' && s.split_status !== 'declined')
    .reduce((p, c) => p.plus(c.amount || 0), new BN(0));
  if (billed.gt(0)) {
    return true;
  }
  return false;
}

function canCheckInstoreReward(tx) {
  const instoreRewardChecked = tx?.extra_data?.instore_reward_checked;
  const tags = tx?.extra_data?.tags;
  if (instoreRewardChecked) {
    return false;
  }
  if (!tags?.includes('item')) {
    return false;
  }
  if (!tx.splits) {
    return false;
  }
  if (!tx.splits.find(s => s.split_type === 'payment')) {
    return false;
  }
  return true;
}

function canVoid(tx) {
  if (!tx.splits || !tx.amount) {
    return false;
  }
  const tags = tx?.extra_data?.tags;
  if (tags?.includes('customer-service')) {
    return true;
  }
  if (!tags?.includes('gift-card')) {
    return false;
  }
  if (tx.splits.length === 1 && tx.splits[0].split_status === 'created') {
    return true;
  }
  return false;
}

function splitTotal(tx) {
  return tx.splits.reduce((value, cur) => {
    if (cur.split_status !== 'voided' && cur.split_status !== 'declined') {
      return value.plus(cur.amount || 0);
    }
    return value;
  }, new BN(0));
}

function lineItemTotal(tx) {
  return tx.extra_data?.line_items
    ?.reduce((value, cur) => value.plus(cur.line_total || 0), new BN(0));
}

function canRefund(tx) {
  if (!tx?.splits || isADSTransaction(tx)) {
    return false;
  }

  const balance = tx.splits
    .filter(s => s.split_status !== 'voided' && s.split_status !== 'declined')
    .reduce((p, c) => p.plus(c.amount || 0), new BN(0));

  return !tx.splits.find(s => s.split_type === 'loss') || balance.lt(0);
}

function canIncreaseLoss(tx) {
  if (!tx?.splits || isADSTransaction(tx)) {
    return false;
  }

  const balance = tx.splits
    .filter(s => s.split_status !== 'voided' && s.split_status !== 'declined')
    .reduce((p, c) => p.plus(c.amount || 0), new BN(0));

  return tx.splits.find(s => s.split_type === 'loss') && balance.gt(0);
}

function canDiscount(tx) {
  return canRebill(tx)
    && !tx.splits.find(s => s.counterparty === 'gasbuddy' && s.split_type === 'discount');
}

function canDelete(tx) {
  const isCardTransaction = txn => txn?.splits?.find(s => s.split_type === 'purchase');
  const isStatementTransaction = txn => txn?.extra_data?.tags?.includes('statement');
  if (!tx?.splits
    || tx.status === 'deleted'
    || isADSTransaction(tx)
    || isCardTransaction(tx)
    || isStatementTransaction(tx)
  ) {
    return false;
  }
  const billed = tx.splits
    .filter(s => s.split_status !== 'voided' && s.split_status !== 'declined')
    .filter(s => !(s.counterparty === 'gasbuddy' && s.split_type === 'earnings'))
    .reduce((p, c) => p.plus(c.amount || 0), new BN(0));
  if (billed.isZero()) {
    return true;
  }
  return false;
}

function isDiscounted(tx, l) {
  return !(new BN(l.discount || 0).isZero())
    && (l.unit === 'gallon' || l.gallons || l.force_discount);
}

function discountAmount(l) {
  if (new BN(l.amount || 0).isZero()) {
    if (l.extra_data && l.extra_data.deferred) {
      return (
        <i>
[
          {new BN(l.extra_data.deferred).toFixed(2)}
]
        </i>
      );
    }
    return '-';
  }
  return new BN(l.amount || 0).toFixed(2);
}

function hasUnearnedGasback(data) {
  return data?.splits?.find(s => s.counterparty !== 'ads' && s.split_type === 'earnings' && s.split_status === 'created');
}

function noDiscount(tx) {
  return tx.extra_data && tx.extra_data.line_items
    && !tx.extra_data.line_items.find(l => isDiscounted(tx, l));
}

function canWaiveNsf(tx) {
  const nsfSplit = tx.splits && tx.splits.find(s => s.split_type === 'payment'
    && s.external_transaction_id
    && s.external_transaction_id.startsWith('chargeback')
    && s.extra_data
    && (s.extra_data.code === 'R01' || s.extra_data.code === 'R09'));

  return nsfSplit && splitTotal(tx).gt(0);
}

function canMove(tx) {
  // eslint-disable-next-line camelcase
  const tags = tx?.extra_data?.tags;
  return tags && tags.length === 1 && tags[0] === 'gasback';
}

function parkwhizBookingId(tx) {
  const split = tx.splits && tx.splits.find(s => (
    s.split_type === 'purchase' && s.counterparty === 'parkwhiz'
  ));
  return split && split.external_transaction_id;
}

class TxDetail extends React.Component {
  state = {
    loading: false,
  }

  forceDiscount = (e, l) => {
    e.preventDefault();
    this.setState({ loading: true }, async () => {
      try {
        const { data, fetch: fetchFn } = this.props;
        const { account_id: aid, transaction_id: tid } = data;
        const { status, body } = await fetch(`/payments/transaction/${aid}/${tid}/force-discount`, {
          index: data.extra_data.line_items.indexOf(l),
          item: l,
        });
        if (status !== 200) {
          this.setState({ rebillError: body, loading: false });
        } else {
          this.setState({ loading: false }, () => {
            fetchFn();
          });
        }
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  emailReceipt = (e) => {
    e.preventDefault();
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        await fetch(`/payments/transaction/${aid}/${tid}/email-receipt`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  deleteTx = (e) => {
    e.preventDefault();
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        await fetch(`/payments/transaction/${aid}/${tid}/delete`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  voidTx = () => {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        await fetch(`/payments/transaction/${aid}/${tid}/void`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  checkInstoreReward = () => {
    this.setState({ loading: true }, async () => {
      const { data, fetch: fetchFn } = this.props;
      const { account_id: accountId, transaction_id: transactionId } = data;
      try {
        await fetch(`/payments/transaction/${accountId}/${transactionId}/instore-reward`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (e) {
        this.setState({ rebillError: e.message, loading: false });
      }
    });
  }

  runDiscount = () => {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        await fetch(`/payments/transaction/${aid}/${tid}/discount`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  waiveNsf = () => {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        await fetch(`/payments/transaction/${aid}/${tid}/waive-nsf`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  reconcileParkwhiz = () => {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid }, data, fetch: fetchFn } = this.props;
      const bookingId = parkwhizBookingId(data);
      try {
        await fetch(`/payments/transaction/${aid}/parkwhiz/${bookingId}/reconcile-parkwhiz`, {});
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  viewParkwhizBooking = () => {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid }, data } = this.props;
      const bookingId = parkwhizBookingId(data);
      try {
        const { body } = await fetch(`/payments/transaction/${aid}/parkwhiz/${bookingId}`, null, 'GET');
        this.setState({ loading: false, parkwhizBooking: body });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  viewAppDetails = () => {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid } } = this.props;
      try {
        const { body } = await fetch(`/payments/transaction/${aid}/${tid}/app-details`, null, 'GET', {
          responseType: 'text',
        });
        this.setState({ loading: false, appDetails: body });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  prev = async (e) => {
    e.preventDefault();
    this.setState({ loading: true });
    const { data: { account_id: aid, transaction_id: tid } } = this.props;
    const { body } = await fetch(`/payments/transaction/${aid}/${tid}/previous`);
    this.goTx(aid, body);
  }

  next = async (e) => {
    e.preventDefault();
    this.setState({ loading: true });
    const { data: { account_id: aid, transaction_id: tid } } = this.props;
    const { body } = await fetch(`/payments/transaction/${aid}/${tid}/next`);
    this.goTx(aid, body);
  }

  goTx(aid, body) {
    if (body && body.transaction_id) {
      this.setState({ loading: false }, () => {
        const { history } = this.props;
        history.push(`/payments/transaction/${aid}/${body.transaction_id}`);
      });
    } else {
      this.setState({ loading: false });
    }
  }

  changeBilling(type) {
    return () => {
      this.setState({ loading: true }, async () => {
        const { data, fetch: fetchFn } = this.props;
        const { account_id: aid, transaction_id: tid } = data;
        try {
          await fetch(`/payments/transaction/${aid}/${tid}/${type}`, {});
          this.setState({ loading: false }, () => {
            fetchFn();
          });
        } catch (error) {
          this.setState({ rebillError: error.message, loading: false });
        }
      });
    };
  }

  cancelDwolla(split) {
    this.setState({ loading: true, isConfirmingDwolla: false }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        const { status, body } = await fetch(`/payments/transaction/${aid}/${tid}/cancel-dwolla`, {
          split_id: split.split_id,
        });
        if (status >= 300) {
          throw new Error(`Cancellation attempt failed.
            ${JSON.stringify(body, null, '\t')}`);
        }
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  retryDwolla(split) {
    this.setState({ loading: true }, async () => {
      const { data: { account_id: aid, transaction_id: tid }, fetch: fetchFn } = this.props;
      try {
        const { status, body } = await fetch(`/payments/transaction/${aid}/${tid}/retry-dwolla`, {
          split_id: split.split_id,
        });
        if (status >= 300) {
          throw new Error(`Retry attempt failed.
            ${JSON.stringify(body, null, '\t')}`);
        }
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ rebillError: error.message, loading: false });
      }
    });
  }

  renderDwollaCancelLink(l) {
    if (l.counterparty === 'dwolla' && l.split_status === 'authorized') {
      const { isConfirmingDwolla } = this.state;
      return (
        <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
          <Button
            size="mini"
            floated="right"
            negative
            compact
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.setState({ isConfirmingDwolla: true });
            }}
          >
              Attempt Cancel
          </Button>
          <Confirm
            header="Are you sure?"
            content="Cancellation may not work if it's past the cutoff time, and can take a few minutes to complete and reflect the status on this page."
            open={isConfirmingDwolla}
            onCancel={() => this.setState({ isConfirmingDwolla: false })}
            onConfirm={() => this.cancelDwolla(l)}
          />
        </Auth>
      );
    }
    return null;
  }

  renderDwollaRetryLink(l) {
    if (l.counterparty === 'dwolla' && (l.split_status === 'created' || l.split_status === 'authorizing')) {
      return (
        <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
          <Button
            size="mini"
            floated="right"
            negative
            compact
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.retryDwolla(l);
            }}
          >
            Retry
          </Button>
        </Auth>
      );
    }
    return null;
  }

  render() {
    const { data, loading, fetch: fetchFn, history } = this.props;
    const { rebillError, loading: stateLoading, appDetails, parkwhizBooking } = this.state;
    // Single gate for the blocked-billing state. Every allow-bill CTA is suppressed
    // on exactly this condition, so a suppressed button is always replaced by the
    // pill rather than leaving the row with no explanation at all.
    const billingBlockedPill = Boolean(data) && shouldShowBillingBlockedPill({
      canRebill: canRebill(data),
      isAds: isADSTransaction(data),
      audit: data.audit,
    });
    if (rebillError) {
      return (
        <Modal open>
          <Modal.Header>Billing Failed</Modal.Header>
          <Modal.Content>{rebillError}</Modal.Content>
          <Modal.Actions>
            <Button color="green" inverted onClick={() => this.setState({ rebillError: null })}>
              <Icon name="checkmark" />
              {' '}
OK
            </Button>
          </Modal.Actions>
        </Modal>
      );
    }
    const earnSplit = data.splits && data.splits.find(s => s.split_type === 'earnings');
    const isClo = earnSplit && earnSplit.counterparty === 'empyr';
    const locationInfo = isClo ? (
      <div>
      Empyr:
        {' '}
        <b>
          {earnSplit.extra_data.empyr_offer && earnSplit.extra_data.empyr_offer.venue_name}
        </b>
      </div>
    ) : (data.location && (
    <div>
      <b>{data.location.dealer.name}</b>
      {' '}
(
      {data.location.brand.name}
)
      <br />
      {addr(data.location.address)}
      <div>
        <b>Wex</b>
        {' '}
#
        {data.location.internal_name}
        <br />
        <b>GasBuddy POI </b>
        {data.location?.poi_id ? (
          <a href={`/payments/location/${data.location.poi_id}`}>{`#${data.location.poi_id}`}</a>
        ) : 'n/a'}
      </div>
    </div>
    ));
    return (
      <Segment>
        <Dimmer active={loading || stateLoading}>
          <Loader />
        </Dimmer>

        <div>
          <Button.Group floated="right">
            <Button icon onClick={this.prev}><Icon name="left chevron" /></Button>
            <Button icon onClick={this.next}><Icon name="right chevron" /></Button>
          </Button.Group>
          <h1>
Transaction #
            <span>{data.transaction_id}</span>
          </h1>
          <div>
Account:
            <NavLink to={`/identity/users/${data.account_id}`}>{accountTagLine(data.account)}</NavLink>
          </div>
          {isClo && earnSplit.extra_data.empyr_card && (
          <div>
            {' '}
Empyr Card:
            <b>
              {earnSplit.extra_data.empyr_card.cardType}
            &nbsp;ending&nbsp;in&nbsp;
              {earnSplit.extra_data.empyr_card.last4}
            </b>
          </div>
          )}
          <b>{data.created && moment(data.created).format('MMM DD, YYYY hh:mm:ss a ZZ')}</b>
          {(data && data.extra_data)
            ? (
              <JSONTree
                inverted
                hideRoot
                data={data.extra_data}
              />
            ) : null
          }
          {data && data.extra_data && data.extra_data.noRebill
            && (
            <Message error>
              Auto-Rebill BLOCKED
              <Auth forRoles={[Role.Superuser, Role.PaymentRunBilling]} hideUnauthorized>
                <Button style={{ marginLeft: 50 }} size="small" onClick={this.changeBilling('allow-auto-bill')}>Allow Auto-Rebill</Button>
              </Auth>
            </Message>
            )
          }
          {data && data.disposition && data.disposition.action === 'willRebill' && data.disposition.rebill_date
            && (
            <Message warning>
              Will rebill on or after
              {' '}
              {moment(data.disposition.rebill_date).format('MMM DD, YYYY hh:mm:ss a ZZ')}
            </Message>
            )
          }
          {data && data.status === 'deleted'
            && (
            <Message warning>
              This transaction is
              {' '}
              <i>deleted,</i>
              {' '}
meaning it will not appear
              in the customer transaction history.
            </Message>
            )
          }
          <Divider />
          {locationInfo}
          <h3>Line Items</h3>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1} textAlign="center">Discount</Table.HeaderCell>
                <Table.HeaderCell>Item Name</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Quantity</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Unit Price</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Line Total</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.extra_data && data.extra_data.line_items
                && data.extra_data.line_items.map((l, ix) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Table.Row key={`${ix}.${l.name}`}>
                    <Table.Cell textAlign="center">
                      {isDiscounted(data, l) && <span style={{ color: 'green' }} role="img" aria-label="check">&#10004;</span>}
                      {!isDiscounted(data, l) && canDiscount(data)
                        && <a href="/" onClick={e => this.forceDiscount(e, l)}>force</a>
                      }
                    </Table.Cell>
                    <Table.Cell>{l.name}</Table.Cell>
                    <Table.Cell>{l.description}</Table.Cell>
                    <Table.Cell textAlign="right">{l.quantity}</Table.Cell>
                    <Table.Cell textAlign="right">
$
                      {l.unit_price}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
$
                      {l.line_total}
                    </Table.Cell>
                  </Table.Row>
                ))}
              <Table.Row>
                <Table.Cell textAlign="right" colSpan="5">Total</Table.Cell>
                <Table.Cell textAlign="right">
$
                  {data.extra_data && data.extra_data.line_items && lineItemTotal(data).toFixed(2)}
                  {data.extra_data && !data.extra_data.line_items && new BN(data.amount || 0).toFixed(2)}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          <Divider />
          <h3>Splits</h3>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>#</Table.HeaderCell>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Counterparty</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Amount</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.splits && data.splits.map((l, ix) => (
                <Popup
                  key={l.split_id}
                  wide="very"
                  on="click"
                  trigger={(
                    <Table.Row {...splitRowAttributes(l)}>
                      <Table.Cell>{ix + 1}</Table.Cell>
                      <Table.Cell>
                        {this.renderDwollaCancelLink(l)}
                        {this.renderDwollaRetryLink(l)}
                        {moment(l.created_at).format('MMM DD, YYYY hh:mm:ss a ZZ')}
                      </Table.Cell>
                      <Table.Cell>{l.split_type}</Table.Cell>
                      <Table.Cell>{l.counterparty}</Table.Cell>
                      <Table.Cell>{l.split_status}</Table.Cell>
                      <Table.Cell textAlign="right">{discountAmount(l)}</Table.Cell>
                    </Table.Row>
)}
                  header="Split Detail"
                  content={(
                    <div style={{ minWidth: 500 }}>
                      <JSONTree
                        inverted
                        shouldExpandNode={() => true}
                        data={l}
                      />
                    </div>
)}
                />
              ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.Cell colSpan={5} textAlign="right">
                  Balance Due:
                </Table.Cell>
                <Table.HeaderCell textAlign="right">
                  {data && data.splits && splitTotal(data).toFixed(2)}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
          {canRebill(data) && (
            <Auth forRoles={[Role.Superuser, Role.PaymentRunBilling]} hideUnauthorized>
              <Button onClick={this.changeBilling('rebill')}>Run Billing For This Transaction</Button>
            </Auth>
          )}
          {canRebill(data) && !data.extra_data.noRebill && !isADSTransaction(data) && (
            <Auth forRoles={[Role.Superuser, Role.PaymentRunBilling]} hideUnauthorized>
              <DelayModal
                accountId={data && data.account_id}
                transactionId={data && data.transaction_id}
              />
            </Auth>
          )}
          {canCheckInstoreReward(data) && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.checkInstoreReward}>Instore Reward</Button>
            </Auth>
          )}
          {canRefund(data)
            && (
            <Auth forRoles={[Role.Superuser, Role.PaymentRefund]} hideUnauthorized>
              <RefundModal
                accountId={data && data.account_id}
                transactionId={data && data.transaction_id}
                onRefund={fetchFn}
              />
            </Auth>
            )
          }
          {canIncreaseLoss(data)
            && (
              <Auth forRoles={[Role.Superuser, Role.PaymentRefund]} hideUnauthorized>
                <RefundModal
                  accountId={data && data.account_id}
                  transactionId={data && data.transaction_id}
                  onRefund={fetchFn}
                />
              </Auth>
            )
          }
          {canMove(data)
            && (
            <Auth forRoles={[Role.Superuser, Role.PaymentRefund]} hideUnauthorized>
              <MoveModal
                history={history}
                accountId={data && data.account_id}
                transactionId={data && data.transaction_id}
              />
            </Auth>
            )
          }
          {

          }
          {wasBilled(data)
            && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.emailReceipt}>Email Receipt</Button>
            </Auth>
            )
          }
          {canDelete(data)
            && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.deleteTx}>Delete From Tx History</Button>
            </Auth>
            )
           }
          {canVoid(data)
            && (
              <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
                <Button onClick={this.voidTx}> Void Transaction </Button>
              </Auth>
            )}
          {canDiscount(data) && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.runDiscount}>Compute Discount</Button>
            </Auth>
          )}
          {canDiscount(data) && noDiscount(data) && !forceBilled(data)
            && !billingBlockedPill && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.changeBilling('allow-bill')}>Allow Billing With No Discount</Button>
            </Auth>
          )}
          {billingBlockedPill
            && (
              <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
                <Label
                  color="red"
                  size="large"
                  style={{
                    margin: '0 0.25em 0 0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: '2.57142857rem',
                  }}
                >
                  Billing Blocked: No Billable Bank Instrument
                </Label>
              </Auth>
            )}
          {canRebill(data) && !forceBilled(data)
            && (!(canDiscount(data) && noDiscount(data)))
            && !isADSTransaction(data)
            && !billingBlockedPill
            && (
              <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
                <Button onClick={this.changeBilling('allow-bill')}>Unblock Billing</Button>
              </Auth>
            )}
          {canWaiveNsf(data) && (
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.waiveNsf}>Waive NSF Fee</Button>
            </Auth>
          )}
          {parkwhizBookingId(data) && [
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.viewParkwhizBooking}>View ParkWhiz Booking</Button>
            </Auth>,
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <Button onClick={this.reconcileParkwhiz}>Reconcile with ParkWhiz Booking</Button>
            </Auth>,
          ]}
          {hasUnearnedGasback(data) && (
            <Auth forRoles={[Role.Superuser, Role.PaymentCredit, Role.ManualGasBackSync]} hideUnauthorized>
              <AwardGasbackModal accountId={data?.account_id} transactionId={data?.transaction_id} fetch={fetchFn} />
            </Auth>
          )}
          <Auth forRoles={[Role.Superuser, Role.PaymentView]} hideUnauthorized>
            <Button onClick={this.viewAppDetails}>View in-app transaction details</Button>
          </Auth>
          <Auth forRoles={[Role.Superuser, Role.PaymentView]} hideUnauthorized>
            <TxDetailModal transactionId={data.transaction_id} accountId={data.account_id} />
          </Auth>
          {getRecentFailedChargeAttempts(data.audit).length > 0 && (
            <div>
              <h3>Charge Attempts</h3>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Error Code</Table.HeaderCell>
                    <Table.HeaderCell>Error Message</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {getRecentFailedChargeAttempts(data.audit).map(a => (
                    <Table.Row key={a.payment_transaction_audit_id}>
                      <Table.Cell>{moment(a.created).format('MMM DD, YYYY hh:mm:ss a ZZ')}</Table.Cell>
                      <Table.Cell>{a.extra_data && a.extra_data.code}</Table.Cell>
                      <Table.Cell>{a.extra_data && a.extra_data.message}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
          <h3>Audit Log</h3>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Split</Table.HeaderCell>
                <Table.HeaderCell>Event</Table.HeaderCell>
                <Table.HeaderCell>Id</Table.HeaderCell>
                <Table.HeaderCell>From</Table.HeaderCell>
                <Table.HeaderCell>To</Table.HeaderCell>
                <Table.HeaderCell>Extra Info</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.audit && data.audit.map(a => (
                <Table.Row key={a.payment_transaction_audit_id}>
                  <Table.Cell>{moment(a.created).format('MMM DD, YYYY hh:mm:ss a ZZ')}</Table.Cell>
                  <Table.Cell>{splitIndex(data, a.payment_transaction_split_id)}</Table.Cell>
                  <Table.Cell>{a.event}</Table.Cell>
                  <Table.Cell>{a.event_id}</Table.Cell>
                  <Table.Cell>{a.from_status}</Table.Cell>
                  <Table.Cell>{a.to_status}</Table.Cell>
                  <Table.Cell>{pre(a.extra_data)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
        {process.env.NODE_ENV !== 'production'
          && <TransactionTools transaction={data} onChange={fetchFn} />
        }
        <Modal
          closeIcon
          closeOnEscape
          open={!!appDetails}
          onClose={() => this.setState({ appDetails: null })}
          size="mini"
        >
          <Modal.Content scrolling>
            <style>
              {' '}
              {`
              #inAppModal #container, #inAppModal #container>div {
                min-height: initial !important;
              }
            `}
              {' '}

            </style>
            {/* eslint-disable-next-line react/no-danger */}
            <div id="inAppModal" className="content" dangerouslySetInnerHTML={{ __html: appDetails }} />
          </Modal.Content>
          <Modal.Actions>
            <Button color="green" inverted onClick={() => this.setState({ appDetails: null })}>
              <Icon name="checkmark" />
              {' '}
OK
            </Button>
          </Modal.Actions>
        </Modal>
        <Modal
          closeIcon
          closeOnEscape
          open={!!parkwhizBooking}
          onClose={() => this.setState({ parkwhizBooking: null })}
          size="mini"
        >
          <Modal.Content scrolling>
            <JSONTree
              inverted
              hideRoot
              data={parkwhizBooking}
            />
          </Modal.Content>
          <Modal.Actions>
            <Button color="green" inverted onClick={() => this.setState({ parkwhizBooking: null })}>
              <Icon name="checkmark" />
              {' '}
OK
            </Button>
          </Modal.Actions>
        </Modal>
      </Segment>
    );
  }
}

TxDetail.propTypes = {
  data: PT.shape({ created: PT.string, splits: PT.array, extra_data: PT.object }),
  loading: PT.bool,
  fetch: PT.func.isRequired,
  history: PT.shape({ push: PT.func }).isRequired,
};

TxDetail.defaultProps = {
  data: {},
  loading: false,
};

function dataUrl({ account, id, counterparty }) {
  if (counterparty) {
    return `/payments/transaction/external/${counterparty}/${id}`;
  }
  if (account) {
    return `/payments/transaction/${account}/${id}`;
  }
  return `/payments/transaction/${id}`;
}

export const Transaction = withRouter(({ match, history }) => (
  <Auth forRoles={[Role.Superuser, Role.PaymentView]}>
    <Nav>
      <WithData url={dataUrl(match.params)}>
        <TxDetail history={history} />
      </WithData>
    </Nav>
  </Auth>
));

Transaction.propTypes = {
  match: PT.shape({ params: PT.shape({ id: PT.string.isRequired }).isRequired }).isRequired,
};
